import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GetMeResponseData } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { FETCH_ME_QUERY_KEY } from './me-query-keys';

export type FetchMeResult = {
    status: 'error' | 'success' | 'pending';
    data?: GetMeResponseData;
    refetch: () => void;
};

/**
 * The signed-in user. Read once while the app starts — nothing renders until
 * this, the companies, the devices and the invites have all arrived.
 */
export function useFetchMe(): FetchMeResult {
    // The client is swapped out on token refresh, so never capture it:
    // read `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const { status, data, refetch } = useQuery({
        queryKey: [FETCH_ME_QUERY_KEY],
        queryFn: () => {
            return openApiRef.current.me
                .getMe()
                .then((response) => response.data.data);
        }
    });

    return {
        status,
        data,
        refetch: () => {
            void refetch();
        }
    };
}
